import { useState, useCallback } from 'react';
import { message } from 'antd';
import { AxiosError } from 'axios';
import { j214Api, FJ510, FJ511, J214CreateRequest, ValidationError } from '../../../services/j214Api';
import { Mode } from './useJ214Mode';

export function useJ214Record() {
  const [record, setRecord] = useState<FJ510 | null>(null);
  const [formData, setFormData] = useState<Partial<FJ510>>({});
  const [details, setDetails] = useState<Partial<FJ511>[]>([]);
  const [loading, setLoading] = useState(false);

  const loadRecord = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const data = await j214Api.get(id);
      setRecord(data);
      setFormData({ ...data });
      setDetails(data.details || []);
    } catch {
      message.error('載入失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  const setField = useCallback((key: keyof FJ510, value: FJ510[keyof FJ510]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  const getField = useCallback((key: keyof FJ510): FJ510[keyof FJ510] => {
    return (formData as Record<string, FJ510[keyof FJ510]>)[key] ?? '';
  }, [formData]);

  const buildRequest = useCallback((): J214CreateRequest => {
    const toDateStr = (v: unknown): string | null => {
      if (!v) return null;
      const s = String(v);
      return s.length >= 10 ? `${s.substring(0, 10)}T00:00:00Z` : null;
    };
    return {
      fj510_11: toDateStr(formData.fj510_11),
      fj510_12: (formData.fj510_12 as string) || '',
      fj510_13: (formData.fj510_13 as string) || '',
      fj510_131: (formData.fj510_131 as string) || '',
      fj510_14: (formData.fj510_14 as string) || '',
      fj510_15: (formData.fj510_15 as string) || '',
      fj510_16: (formData.fj510_16 as string) || '',
      fj510_17: Number(formData.fj510_17) || 0,
      fj510_18: Number(formData.fj510_18) || 0,
      fj510_19: Number(formData.fj510_19) || 0,
      fj510_20: Number(formData.fj510_20) || 0,
      fj510_21: toDateStr(formData.fj510_21),
      fj510_22: (formData.fj510_22 as string) || '',
      fj510_24: (formData.fj510_24 as string) || '',
      fj510_25: (formData.fj510_25 as string) || '',
      fj510_26: (formData.fj510_26 as string) || '',
      fj510_27: (formData.fj510_27 as string) || '',
      fj510_30: (formData.fj510_30 as string) || '',
      details: details as FJ511[],
    };
  }, [formData, details]);

  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: FJ510) => void,
  ) => {
    setLoading(true);
    try {
      const req = buildRequest();
      if (mode === 'add') {
        const result = await j214Api.create(req);
        message.success(`新增成功: ${result.fj510_01}`);
        onSuccess(result);
      } else {
        const result = await j214Api.update(formData.fj510_01!, req);
        message.success('修改成功');
        onSuccess(result);
      }
    } catch (err: unknown) {
      const axErr = err as AxiosError<{ errors?: ValidationError[]; error?: string }>;
      if (axErr.response?.status === 422 && axErr.response.data?.errors) {
        const errors = axErr.response.data.errors;
        const summary = errors.slice(0, 5).map((e: { message: string }) => e.message).join('\n');
        const extra = errors.length > 5 ? `\n...共 ${errors.length} 項` : '';
        message.error({ content: summary + extra, duration: 6, style: { whiteSpace: 'pre-line' } });
      } else {
        const errMsg = axErr.response?.data?.error || (err instanceof Error ? err.message : '儲存失敗');
        message.error(errMsg);
      }
    } finally {
      setLoading(false);
    }
  }, [formData, buildRequest]);

  const handleDelete = useCallback(async (onSuccess: () => void) => {
    if (!record?.fj510_01) return;
    setLoading(true);
    try {
      await j214Api.delete(record.fj510_01);
      message.success('刪除成功');
      onSuccess();
    } catch {
      message.error('刪除失敗');
    } finally {
      setLoading(false);
    }
  }, [record]);

  const revertToRecord = useCallback(() => {
    if (record) {
      setFormData({ ...record });
      setDetails(record.details || []);
    }
  }, [record]);

  const resetForAdd = useCallback(() => {
    setRecord(null);
    setFormData({ fj510_80: '', fj510_30: 'NTD' } as Partial<FJ510>);
    setDetails([]);
  }, []);

  const applyResult = useCallback((result: FJ510) => {
    setRecord(result);
    setFormData({ ...result });
    setDetails(result.details || []);
  }, []);

  const addDetailRow = useCallback(() => {
    setDetails(prev => [
      ...prev,
      {
        fj511_02: prev.length + 1,
        fj511_11: '',
        fj511_12: '',
        fj511_13: 0,
        fj511_14: 0,
        fj511_15: 0,
      },
    ]);
  }, []);

  const deleteDetailRow = useCallback((idx: number) => {
    setDetails(prev => prev.filter((_, i) => i !== idx));
  }, []);

  return {
    record, formData, details, loading,
    setDetails,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
    addDetailRow, deleteDetailRow,
  };
}
